import { useRef, useState } from 'react';
import { useAppDispatch } from 'hooks';

import { DashboardBudgetsReq, refreshDashboard } from '~support-at-home/actions/dashboardBudgets';

/**
 * Refresh for the whole Dashboard (TAV-3351): reloads the four widgets at once. `isRefreshing` is true until the last
 * one is back; `error` is set when the refresh failed, and clears on the next press or once the quarter, year or case
 * manager changes (the failure is kept with the args it was for, so `args` must be memoised, as BudgetDashboard's
 * useMemo does, or every render would read as a change). A press while one is in flight is ignored.
 */
export const useDashboardRefresh = (args: DashboardBudgetsReq) => {
  const dispatch = useAppDispatch();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [failure, setFailure] = useState<{ args: DashboardBudgetsReq; error: unknown } | null>(null);
  const inFlight = useRef(false);

  const refresh = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setFailure(null);
    setIsRefreshing(true);
    try {
      await dispatch(refreshDashboard(args));
    } catch (caught) {
      setFailure({ args, error: caught });
    } finally {
      inFlight.current = false;
      setIsRefreshing(false);
    }
  };

  return { refresh, isRefreshing, error: failure?.args === args ? failure.error : null };
};
